import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FolderIcon from "@mui/icons-material/Folder";
import FolderOffIcon from "@mui/icons-material/FolderOff";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AddIcon from "@mui/icons-material/Add";
import { secondAxios } from "../../../../../api/axios";
import useModuleAccess from "../../../../../hooks/useModuleAccess";
import {
  APP_MODULES,
  ALL_ESTIMATION_MODULE_NAMES,
  PLANT_MODULE_LICENCE_MAP,
} from "../../../../../constants/appModules";
import { PLANT_MODULES } from "../../../../../constants/plantModules";
import { getPersonLabel } from "../plantUtils";
import ModuleIconBadge from "../../../../../components/ModuleIconBadge";
import EmptyState from "../../../../../components/EmptyState";
import SubProjectsCrudModal from "../SubProjectsCrudModal";

export default function ProjectHub() {
  const { plantId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isEstimation = searchParams.get("mode") === "estimation";

  const { hasModule, hasAnyModule } = useModuleAccess();

  const [plant, setPlant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modulesModalOpen, setModulesModalOpen] = useState(false);
  const [aimsSending, setAimsSending] = useState(false);

  const fetchPlant = async () => {
    try {
      setLoading(true);
      const res = await secondAxios.get(`/plant/${plantId}`);
      setPlant(res.data);
    } catch (err) {
      console.error("Error fetching project:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (plantId) fetchPlant();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plantId]);

  const handleSendToAIMS = async () => {
    if (!plantId || aimsSending) return;
    try {
      setAimsSending(true);
      await secondAxios.put(`/plant/${plantId}/aims-sent`);
      setPlant((prev) => (prev ? { ...prev, aimsSent: true } : prev));
    } catch (err) {
      console.error("Error sending project to AIMS:", err);
    } finally {
      setAimsSending(false);
    }
  };

  const visibleModules = useMemo(() => {
    const hasEstimationAccess = hasAnyModule(ALL_ESTIMATION_MODULE_NAMES);

    if (isEstimation) {
      return hasEstimationAccess
        ? PLANT_MODULES.filter((module) => module.key === "estimation")
        : [];
    }

    return PLANT_MODULES.filter((module) => {
      if (module.key === "estimation" || module.key === "docRegister")
        return false;
      return hasModule(
        PLANT_MODULE_LICENCE_MAP[module.key] ?? APP_MODULES.EPCM,
      );
    });
  }, [hasModule, hasAnyModule, isEstimation]);

  const docRegisterModule = PLANT_MODULES.find(
    (module) => module.key === "docRegister",
  );
  const hasDocRegisterAccess =
    !isEstimation &&
    Boolean(docRegisterModule) &&
    hasModule(PLANT_MODULE_LICENCE_MAP.docRegister ?? APP_MODULES.EMS);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!plant) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography color="text.secondary">Project not found.</Typography>
      </Box>
    );
  }

  const targetLabel = isEstimation ? "EPCM" : "AIMS";
  const isAimsSent = Boolean(plant.aimsSent);
  const people = Array.isArray(plant.people) ? plant.people : [];
  const subProjects = plant.projects || plant.project;
  const subProjectsArray = Array.isArray(subProjects)
    ? subProjects
    : subProjects
      ? [subProjects]
      : [];
  const clientEnvName = process.env.REACT_APP_CLIENT_NAME;
  const tagCount = subProjectsArray.filter(
    (p) => p && p.clientName === clientEnvName,
  ).length;

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: "auto" }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(-1)}
        sx={{ mb: 2 }}
      >
        Back to Projects
      </Button>

      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ md: "flex-start" }}
            spacing={2}
          >
            <Stack direction="row" spacing={2} alignItems="flex-start">
              <ModuleIconBadge moduleKey="plant" size="lg" />
              <Box>
                <Typography variant="h1">{plant.name}</Typography>
                <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                  {plant.description || "No description"}
                </Typography>

                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ mt: 1.5 }}
                  flexWrap="wrap"
                  useFlexGap
                >
                  {(plant.client?.name || plant.client?.prefix) && (
                    <Chip
                      size="small"
                      label={plant.client?.name || plant.client?.prefix}
                    />
                  )}
                  <Chip
                    size="small"
                    label={
                      aimsSending
                        ? "Sending..."
                        : isAimsSent
                          ? `Sent To ${targetLabel}`
                          : `Send To ${targetLabel}`
                    }
                    icon={isAimsSent ? <CheckCircleIcon /> : <AddIcon />}
                    variant={isAimsSent ? "filled" : "outlined"}
                    color={isAimsSent ? "success" : "primary"}
                    disabled={isAimsSent || aimsSending}
                    onClick={handleSendToAIMS}
                  />
                  <Chip
                    size="small"
                    variant="outlined"
                    color="primary"
                    clickable
                    label={`${tagCount} WBS`}
                    onClick={() => setModulesModalOpen(true)}
                  />
                </Stack>
              </Box>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center">
              {hasDocRegisterAccess && (
                <Button
                  variant="outlined"
                  startIcon={
                    <ModuleIconBadge moduleKey="docRegister" size="xs" />
                  }
                  onClick={() =>
                    navigate(`/plant/${plantId}/${docRegisterModule.path}`)
                  }
                >
                  Document Register
                </Button>
              )}

              {plant.sharepointLink ? (
                <Tooltip title="Open SharePoint directory">
                  <IconButton
                    component="a"
                    href={plant.sharepointLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    color="primary"
                  >
                    <FolderIcon />
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip title="No directory link">
                  <span>
                    <IconButton disabled>
                      <FolderOffIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </Stack>
          </Stack>

          {people.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Project Manager & Team
              </Typography>
              <Stack
                direction="row"
                spacing={0.5}
                flexWrap="wrap"
                useFlexGap
                sx={{ mt: 0.5 }}
              >
                {people.map((person, index) => (
                  <Chip
                    key={
                      typeof person === "object"
                        ? person.id || person.email || index
                        : person
                    }
                    size="small"
                    variant="outlined"
                    label={getPersonLabel(person)}
                  />
                ))}
              </Stack>
            </Box>
          )}
        </CardContent>
      </Card>

      <Typography variant="h5" sx={{ mb: 1.5 }}>
        Modules
      </Typography>

      {visibleModules.length === 0 ? (
        <EmptyState
          moduleKey="plant"
          title="No modules available"
          description="Your licence doesn't include any of this project's modules. Ask your administrator to add one."
        />
      ) : (
        <Grid container spacing={2}>
          {visibleModules.map(({ key, label, description, path }) => (
            <Grid key={key} item xs={12} sm={6} md={4} lg={3}>
              <Card
                sx={{
                  height: "100%",
                  transition: "box-shadow 0.15s ease, transform 0.15s ease",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: (theme) =>
                      `0 0 0 1px ${theme.palette.primary.main}, 0 8px 20px rgba(15, 23, 42, 0.12)`,
                  },
                }}
              >
                <CardActionArea
                  onClick={() => navigate(`/plant/${plantId}/${path}`)}
                  sx={{
                    height: "100%",
                    p: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    gap: 1.5,
                  }}
                >
                  <ModuleIconBadge moduleKey={key} size="lg" />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle1" fontWeight={700}>
                      {label}
                    </Typography>
                    {description && (
                      <Typography variant="body2" color="text.secondary">
                        {description}
                      </Typography>
                    )}
                  </Box>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <SubProjectsCrudModal
        open={modulesModalOpen}
        plant={plant}
        onClose={() => setModulesModalOpen(false)}
        onProjectsChange={fetchPlant}
      />
    </Box>
  );
}
